import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/LoadingButton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  getGetOrganizationRolesQueryKey,
  useDeleteRole,
} from '@/api/auth/roles/roles'
import { type UserRoleResponse } from '@/api/auth/missionImpactAuthAPI.schemas'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'

interface DeleteMemberDialogProps {
  organizationId: string
  role: UserRoleResponse
}

/**
 * Dialog that confirms removal of a member from an organization.
 */
export function DeleteMemberDialog({
  organizationId,
  role,
}: DeleteMemberDialogProps) {
  const queryClient = useQueryClient()
  const { handleApiError } = useError();
  const { t } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)

  const { mutate: deleteRole, isPending } = useDeleteRole({
    mutation: {
      onSuccess: () => {
        toast.success(t.dialogs.removeMember.userRemoved)
        void queryClient.invalidateQueries({
          queryKey: getGetOrganizationRolesQueryKey(organizationId),
        })
        setIsOpen(false)
      },
      onError: (error) => {
        handleApiError(error, { context: 'DeleteMemberDialog.deleteRole' })
      },
    },
  })

  const handleDelete = () => {
    deleteRole({
      orgId: organizationId,
      roleId: role.id,
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t.dialogs.removeMember.triggerLabel}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <DialogTitle>{t.dialogs.removeMember.title}</DialogTitle>
          </div>
          <DialogDescription>
            {t.dialogs.removeMember.description.replace('{email}', role.email ?? '')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => { setIsOpen(false); }}
            disabled={isPending}
          >
            {t.dialogs.removeMember.cancel}
          </Button>
          <LoadingButton
            type="button"
            variant="destructive"
            onClick={handleDelete}
            isLoading={isPending}
          >
            {t.dialogs.removeMember.remove}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
